<template>
  <v-row id="accountSettings">
    <v-col md="12" cols="12" class="pa-0">
      <v-card flat tile>
        <v-card-text>
          <!-- Account Settings Section -->
          <v-list-item class="px-0">
            <div class="body-heading-medium">Account Settings</div>
            <v-btn v-if="!isEditingGeneralInfo && user.role === 'parent'" @click="toggleEditingGeneralInfo" icon class="ma-1">
              <v-icon size="16">fas fa-edit</v-icon>
            </v-btn>
          </v-list-item>
          <v-row>
            <v-col v-if="!isEditingGeneralInfo" cols="12" md="12">
              <!-- Avatar -->
              <v-row justify="center" align="center">
                <v-col cols="12" md="1">
                  <v-avatar v-if="user.avatar" slot="icon">
                    <img alt="Avatar" v-bind:src="user.avatar" />
                  </v-avatar>
                  <v-avatar v-else slot="icon" color="grey lighten-2">
                    <v-icon>mdi-account-circle</v-icon>
                  </v-avatar>
                </v-col>
                <v-col cols="12" md="11">
                  <div class="mb-2">Your Avatar</div>
                  <UpdateAvatarModal :userId="user.id" :userType="user.role" />
                </v-col>
              </v-row>

              <!-- Name -->
              <v-list-item class="px-0">
                <v-list-item-content>
                  <v-list-item-title>Name:</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ user.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <!-- Email -->
              <v-list-item two-line class="px-0">
                <v-list-item-content>
                  <v-list-item-title>Email:</v-list-item-title>
                  <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <!-- Phone -->
              <v-list-item two-line class="px-0">
                <v-list-item-content>
                  <v-list-item-title>Phone:</v-list-item-title>
                  <v-list-item-subtitle>{{ user.phone }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <!-- Edit General Info Form -->
            <v-col v-else-if="isEditingGeneralInfo && user.role === 'parent'" cols="12" md="12">
              <v-form :class="{ 'half-width': !isMobile }" v-model="isGeneralInfoFormValid" ref="generalInfoForm" @submit.prevent="updateGeneralInfo">
                <!-- Name -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field label="First Name" name="firstName" outlined dense type="text" required :rules="firstNameRules" v-model="newUserProfile.first_name" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field label="Last Name" name="lastName" outlined dense type="text" required :rules="lastNameRules" v-model="newUserProfile.last_name" />
                  </v-col>
                </v-row>
                <!-- Email -->
                <v-row>
                  <v-col cols="12" md="12">
                    <v-text-field label="Email" name="email" outlined dense type="text" required="true" :rules="emailRules" v-model="newUserProfile.email" />
                  </v-col>
                </v-row>
                <!-- Phone -->
                <v-row>
                  <v-col cols="12" md="12">
                    <VuePhoneNumberInput v-model="newUserProfile.phone" default-country-code="CA" :only-countries="['CA']" color="#354093" error-color="#ff4d4f" no-example />
                  </v-col>
                </v-row>
                <v-btn color="secondary" type="submit" :disabled="!isGeneralInfoFormValid">
                  Save
                </v-btn>
                <v-btn text @click="cancel">Cancel</v-btn>
              </v-form>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="user.role === 'parent'" />

          <!-- Password Section -->
          <v-list-item class="px-0" v-if="user.role === 'parent'">
            <p class="body-heading-medium">Password</p>
          </v-list-item>
          <v-row v-if="user.role === 'parent'">
            <v-col v-if="!isEditingPassword" cols="12" md="12">
              <v-btn @click="toggleEditingPassword" color="secondary">Update Password</v-btn>
            </v-col>
            <!-- Edit Password Form -->
            <v-col v-else cols="12" md="12">
              <v-form :class="{ 'half-width': !isMobile }" v-model="isPasswordFormValid" ref="passowordForm" @submit.prevent="updatePassoword">
                <!-- Old Password -->
                <v-row>
                  <v-col cols="12" md="12">
                    <v-text-field label="Old password" name="oldPassword" outlined dense :append-icon="showOldPassword ? 'mdi-eye' : 'mdi-eye-off'" :type="showOldPassword ? 'text' : 'password'" required :rules="passwordRules" v-model="newUserPassword.oldPassword" @click:append="showOldPassword = !showOldPassword" />
                  </v-col>
                </v-row>
                <!-- Password -->
                <v-row>
                  <v-col cols="12" md="12">
                    <v-text-field label="New password" name="newPassword" outlined dense :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'" :type="showNewPassword ? 'text' : 'password'" required :rules="passwordRules" v-model="newUserPassword.password" @click:append="showNewPassword = !showNewPassword" />
                  </v-col>
                </v-row>
                <!-- Confirm Password -->
                <v-row>
                  <v-col cols="12" md="12">
                    <v-text-field label="Confirm new password" name="confirm_password" outlined dense :append-icon="showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'" :type="showPasswordConfirmation ? 'text' : 'password'" required :rules="confirmPasswordRules" v-model="newUserPassword.passwordConfirmation" @click:append="showPasswordConfirmation = !showPasswordConfirmation" />
                  </v-col>
                </v-row>
                <v-btn color="primary" type="submit" :disabled="!isPasswordFormValid">
                  Save
                </v-btn>
                <v-btn text @click="cancel">Cancel</v-btn>
              </v-form>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="user.role === 'parent'" />

          <!-- Delete Account Section -->
          <v-row v-if="user.role === 'parent'">
            <v-col cols="12" md="12">
              <v-list-item class="px-0">
                <p class="body-heading-medium">Delete Account</p>
              </v-list-item>
              <v-list-item class="px-0">
                <DeleteAccountModal :accountId="user.id" userType="parent" />
              </v-list-item>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
  import DeleteAccountModal from "@/components/DeleteAccountModal";
  import UpdateAvatarModal from "@/components/UpdateAvatarModal";
  import VuePhoneNumberInput from "vue-phone-number-input";

  export default {
    name: "AccountSettings",

    data() {
      return {
        showPasswordConfirmation: false,
        showOldPassword: false,
        showNewPassword: false,
        user: {},
        isEditingGeneralInfo: false,
        isEditingPassword: false,
        newUserProfile: {},
        newUserPassword: {},
        firstNameRules: [(v) => !!v || "First Name is required"],
        lastNameRules: [(v) => !!v || "Last name is required"],
        emailRules: [(v) => !!v || "E-mail is required", (v) => /.+@.+\..+/.test(v) || "E-mail must be valid"],
        phoneRules: [
          (v) => !!v || "Phone number is required",
          (v) => {
            if (/^[0-9\-\()]*$/.test(v)) {
              return true;
            }
            return "Only numbers or '()' or '-' are allowed";
          },
          (v) => (v && v.length >= 10) || "Phone number must be at least 10 numbers",
        ],
        passwordRules: [(v) => !!v || "Password is required"],
        confirmPasswordRules: [(v) => !!v || "Password is required", (v) => v === this.newUserPassword.password || "Password do not match"],
        isGeneralInfoFormValid: false,
        isPasswordFormValid: false,
      };
    },

    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.mobile;
      },
    },

    methods: {
      toggleEditingGeneralInfo() {
        this.isEditingPassword = false;
        this.newUserProfile = this.$formatter.extend(this.user);
        this.isEditingGeneralInfo = true;
      },

      toggleEditingPassword() {
        this.isEditingGeneralInfo = false;
        this.isEditingPassword = true;
      },

      updateGeneralInfo() {
        this.$store.commit("setLoadingStatus", true);

        this.$http
          .put("/parents", this.newUserProfile)
          .then((response) => {
            //FIXME: This is walk around, since the response of the API to update parent info does not have the 'role' property.
            this.user = this.$formatter.extend(this.$store.getters.userInfo, response.data.parent);
            this.$sessionUser._updateUserInfo(this.user);
            this.$store.commit("setUserInfo", this.user);
            this.isEditingGeneralInfo = false;
            this.$store.commit("setLoadingStatus", false);
          })
          .catch((error) => {
            console.error(error);
            this.$store.commit("setError", {
              message: "Could not update user's profile, please try again later",
              type: "error",
            });
            this.isEditingGeneralInfo = false;
            this.$store.commit("setLoadingStatus", false);
          });
      },

      updatePassoword() {
        this.$store.commit("setLoadingStatus", true);

        this.$http
          .put("/parents/password/reset", {
            old_password: this.newUserPassword.oldPassword,
            new_password: this.newUserPassword.password,
            new_password_confirmation: this.newUserPassword.passwordConfirmation,
          })
          .then((response) => {
            this.isEditingPassword = false;
            this.$store.commit("setLoadingStatus", false);
          })
          .catch((error) => {
            console.error(error);
            this.$store.commit("setError", {
              message: "Could not update user's password, please try again later",
              type: "error",
            });
            this.isEditingPassword = false;
            this.$store.commit("setLoadingStatus", false);
          });
      },

      cancel() {
        this.newUserProfile = {};
        this.newUserPassword = {};
        this.isEditingGeneralInfo = false;
        this.isEditingPassword = false;
      },
    },

    components: { DeleteAccountModal, UpdateAvatarModal, VuePhoneNumberInput },

    created() {
      this.$http
        .get("/profile")
        .then((response) => {
          //TODO move this into Vuex module
          this.user = response.data.profile;
          //FIXME: This is walk around, since the response of the API to get user profile does not have the 'role' property.
          this.user.role = this.$store.getters.userInfo.role;
        })
        .catch((error) => {
          console.error(error);
          this.$store.commit("setError", {
            message: "Could not retrieve user's information, please try again later",
            type: "error",
          });
        });
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/GeneralSettings/AccountSettings.vue